import {ActionMenu, ActionList, IconButton} from '@primer/react'
import {KebabHorizontalIcon, AlertIcon, CommentDiscussionIcon, GearIcon, ChevronRightIcon} from '@primer/octicons-react'
import {useState, useRef, useCallback} from 'react'
import {CommentsSidePanel} from './CommentsSidePanel'
import {AlertsSidePanel} from './AlertsSidePanel'
import {DiffViewSettingsMenuContent} from '@github-ui/diff-view-settings'
import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useAnnotations, useThreadCommentCount} from '../../stores/selectors'
import type {PageLimits} from '../../page-data/payloads/files'
import type {PullRequest} from '../../page-data/payloads/toolbar'

export interface ToolbarMoreMenuProps {
  pageLimits: PageLimits
  pullRequest: PullRequest
  hideWhitespace: boolean
  onUpdateWhitespace: (value: boolean) => void
  additionalPreferences?: React.ReactNode
}

export function ToolbarMoreMenu({
  pageLimits,
  pullRequest,
  hideWhitespace,
  onUpdateWhitespace,
  additionalPreferences,
}: ToolbarMoreMenuProps) {
  const [commentsSidePanelIsOpen, setCommentsSidePanelIsOpen] = useState(false)
  const [alertsSidePanelIsOpen, setAlertsSidePanelIsOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const annotationsById = useAnnotations()
  const diffCommentCount = useThreadCommentCount()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const annotations = annotationsById ? Object.values(annotationsById) : []
  const totalAnnotationsCount = annotations.length

  const handleOpenComments = useCallback(() => {
    sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
    ensurePreviousActiveDialogIsClosed()
    setCommentsSidePanelIsOpen(true)
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent])

  const handleOpenAlerts = useCallback(() => {
    sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
    ensurePreviousActiveDialogIsClosed()
    setAlertsSidePanelIsOpen(true)
    setIsMenuOpen(false)
  }, [sendPullRequestAnalyticsEvent])

  return (
    <>
      <ActionMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <ActionMenu.Anchor>
          <IconButton
            ref={menuButtonRef}
            icon={KebabHorizontalIcon}
            variant="default"
            size="small"
            aria-label="More options"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.Group>
              <ActionList.Item onSelect={handleOpenComments}>
                <ActionList.LeadingVisual>
                  <CommentDiscussionIcon />
                </ActionList.LeadingVisual>
                Comments
                {diffCommentCount > 0 && <ActionList.TrailingVisual>{diffCommentCount}</ActionList.TrailingVisual>}
              </ActionList.Item>
              {totalAnnotationsCount > 0 && (
                <ActionList.Item onSelect={handleOpenAlerts}>
                  <ActionList.LeadingVisual>
                    <AlertIcon />
                  </ActionList.LeadingVisual>
                  Alerts
                  <ActionList.TrailingVisual>{totalAnnotationsCount}</ActionList.TrailingVisual>
                </ActionList.Item>
              )}
            </ActionList.Group>
            <ActionList.Divider />
            <ActionList.Group>
              <ActionMenu open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
                <ActionMenu.Anchor>
                  <ActionList.Item>
                    <ActionList.LeadingVisual>
                      <GearIcon />
                    </ActionList.LeadingVisual>
                    Settings
                    <ActionList.TrailingVisual>
                      <ChevronRightIcon />
                    </ActionList.TrailingVisual>
                  </ActionList.Item>
                </ActionMenu.Anchor>
                <ActionMenu.Overlay side="outside-bottom" align="end">
                  <ActionList>
                    <DiffViewSettingsMenuContent
                      reloadOnWhitespaceChange
                      onUpdateWhitespace={onUpdateWhitespace}
                      defaultHideWhitespace={hideWhitespace}
                      additionalPreferences={additionalPreferences}
                    />
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      <CommentsSidePanel
        isOpen={commentsSidePanelIsOpen}
        pageLimits={pageLimits}
        pathName={pullRequest.pathName}
        pullRequestId={pullRequest.id}
        toggleSidesheetRef={menuButtonRef}
        onClose={() => setCommentsSidePanelIsOpen(false)}
      />

      {totalAnnotationsCount > 0 && (
        <AlertsSidePanel
          annotations={annotations}
          isOpen={alertsSidePanelIsOpen}
          pageLimits={pageLimits}
          returnFocusRef={menuButtonRef}
          onClose={() => setAlertsSidePanelIsOpen(false)}
        />
      )}
    </>
  )
}

try{ ToolbarMoreMenu.displayName ||= 'ToolbarMoreMenu' } catch {}